import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import styled from 'styled-components';
import { BaseComponent } from "../../../components/BaseComponent";
import { HBox, VBox } from "../../../components/Box";
import { FormField } from "../../../components/FormFields";
import { Address } from "../../../components/Address";
import { Button } from "../../../components/Buttons";
import { Dropdown } from "../../../components/Dropdown";
import { Content } from "../../../components/Content";
import { fetchCompanies, fetchCompany } from "../../Companies/actions";
import { fetchUser, fetchUsersList } from "../../Users/actions";
import { addSupplier, removeSupplier } from "../actions";
import { Modal } from "../../../components/Modal";
import { Loader } from "../../../components/Loader";
import { selectWorks } from "../../Api/selectors";
import { REQUEST_USER, REQUEST_USERS } from "../../Users/constants";
import { REQUEST_COMPANIES, REQUEST_COMPANY } from "../../Companies/constants";

const Container = styled.div``;

const STATE = {
    supplierType: null,
    supplierCompany: null,
    supplierUser: null,
    supplierAddress: null,
    confirmRemove: false,
    confirmSave: false,
    availableCompanies: null,
    availableUsers: null,
    availableCompanyUsers: null,
    availableAddresses: null,
    usersLoadedForCompanyId: null,
    addressesLoadedForUserId: null,
}

const SUPPLIER_TYPES = {
    INDIVIDUAL: 'individual',
    COMPANY: 'company'
}

/* eslint-disable react/prefer-stateless-function */
class DetailsSupplierComp extends BaseComponent {
    constructor(props) {
        super(props, STATE);
        this.state = {...this.state, ...this.originData};
        this.loadData();
    }

    componentDidMount() {
    }

    loadData() {
        if (this.needCompanyList) {
            this.fetchCompanies();
        }

        if (this.needUsersList) {
            this.fetchUsers();
        }
        if (this.needCompanyUsersList) {
            this.fetchCompanyUsers();
        }

        if (this.needAddressesList) {
            this.fetchAddresses();
        }
    }

    getVehicleProperty(name) {

        return this.props.vehicle.properties.find(p => p.propertyName.name === name)?.propertyValue?.value ?? null;
    }

    get originData() {
        return [
            'supplierType',
            'supplierCompany',
            'supplierUser',
            'supplierAddress',
        ].reduce((acc, name) => {
            acc[name] = this.getVehicleProperty(name);
            return acc;
        }, {});
    }

    get vehicle() {
        return this.props.vehicle;
    }

    get needCompanyList() {

        return this.supplierType === SUPPLIER_TYPES.COMPANY;
    }

    get needUsersList() {

        return this.supplierType === SUPPLIER_TYPES.INDIVIDUAL;
    }

    get needCompanyUsersList() {

        return this.supplierType === SUPPLIER_TYPES.COMPANY && this.state.supplierCompany !== null && this.state.usersLoadedForCompanyId !== this.state.supplierCompany;
    }

    get needAddressesList() {

        return this.supplierUser !== null && this.state.addressesLoadedForUserId !== this.state.supplierUser;
    }

    fetchCompanies() {

        if (this.state.availableCompanies !== null) {
            return;
        }

        this.props.fetchCompanies(0, 1000, data => {
            const availableCompanies = data.items ?? [];
            this.setState(prev => {
                return {
                    ...prev,
                    availableCompanies,
                };
            });
        });
    }

    fetchUsers() {
        if (this.state.availableUsers !== null) {
            return;
        }

        this.props.fetchUsers(0, 1000, data => {
            const availableUsers = data.items ?? [];
            this.setState((prev, props) => {
                return {...prev, availableUsers };
            }, this.loadData);
        });
    }

    fetchAddresses() {
        const userId = this.supplierUser;
        this.props.fetchUser(userId, data => {
            const availableAddresses = data.addresses;
            const addressesLoadedForUserId = data.supplierUser;
            this.setState((prev, props) => {
                return {...prev, availableAddresses, addressesLoadedForUserId };
            }, this.loadData);
        });
    }

    fetchCompanyUsers() {

        const companyId = this.state.supplierCompany;
        this.props.fetchCompany(companyId, data => {
            const availableCompanyUsers = data.users ?? [];
            const usersLoadedForCompanyId = companyId;
            this.setState((prev, props) => {
                return {...prev, availableCompanyUsers, usersLoadedForCompanyId };
            }, this.loadData);
        });
    }

    get supplierTypeOptions() {

        return [
            {
                label: 'Individual',
                value: SUPPLIER_TYPES.INDIVIDUAL,
            },
            {
                label: 'Company',
                value: SUPPLIER_TYPES.COMPANY,
            },
        ];
    }

    get usersOptions() {

        return this.availableUsers?.map(user => {
            return {
                label: this.getUserFullName(user),
                value: user.id.toString(),
            }
        }).filter(o => o.label !== '') ?? [];
    }

    get addressesOptions() {

        return this.availableAddresses?.map(address => {
            return {
                label: `${address.country.name}, ${address.city}, (${address.addressLine1}, ${address.addressLine2})`,
                value: address.id.toString(),
            }
        }) ?? [];
    }

    get companyOptions() {

        return this.availableCompanies?.map(company => {
            return {
                label: this.getCompanyName(company),
                value: company.id.toString(),
            }
        }) ?? [];
    }


    get supplierType() {
        return this.state.supplierType;
    }

    get supplierUser() {
        return this.state.supplierUser;
    }

    get supplierCompany() {
        return this.state.supplierCompany;
    }

    get supplierAddress() {
        return this.state.supplierAddress;
    }

    get availableAddresses() {

        return this.state.availableAddresses;
    }

    get availableUsers() {
        switch(this.supplierType) {
            case SUPPLIER_TYPES.INDIVIDUAL:
                return this.state.availableUsers;

            case SUPPLIER_TYPES.COMPANY:
                return this.state.availableCompanyUsers;

            default:
                return null;
        }
    }

    get availableCompanies() {

        return this.state.availableCompanies;
    }

    get address() {

        return this.availableAddresses?.find(a => a.id === this.supplierAddress);
    }

    get fieldsList() {

        return [
            'supplierType',
            'supplierCompany',
            'supplierUser',
            'supplierAddress',
        ];
    }

    get saveDisabled() {

        return !this.isDirtyForm() || !this.supplierType || !this.supplierUser || !this.supplierAddress;
    }

    get resetDisabled() {

        return !this.isDirtyForm();
    }

    get removeDisabled() {
        const currentSupplierAddress = this.getVehicleProperty('supplierAddress');
        return !currentSupplierAddress;
    }

    getCompanyProperty(company, propertyName) {

        return company.properties.find(p => {
            return p.name.name === propertyName;
        })?.value ?? null;
    }

    getCompanyName(company) {

        return this.getCompanyProperty(company, 'name');
    }

    getUserProperty(user, propertyName) {

        return user.properties.find(p => {
            return p.name.name === propertyName;
        })?.value ?? null;
    }

    getUserFullName(user) {
        const name = this.getUserProperty(user, 'name');
        const surname = this.getUserProperty(user, 'surname');
        let fullName = '';
        fullName += name ? name : '';
        fullName += surname ? ` ${surname}` : '';

        return fullName;
    }

    onSupplierTypeChange(evt) {
        const { value } = evt;
        this.setState((prev, props) => {
            return {
                ...prev,
                supplierType: value,
                supplierCompany: null,
                supplierUser: null,
                supplierAddress: null,
                availableUsers: null,
                availableAddresses: null,
            };
        }, this.loadData);
    }

    onSupplierAddressChange(evt) {
        const { value } = evt;
        this.setState((prev, props) => {
            return {...prev, supplierAddress: +value};
        });
    }

    onSupplierUserChange(evt) {
        const { value } = evt;
        this.setState((prev, props) => {
            return {
                ...prev,
                supplierUser: value,
                supplierAddress: null,
                availableAddresses: null,
            };
        }, this.loadData);
    }

    onSupplierCompanyChange(evt) {
        const { value } = evt;
        this.setState((prev, props) => {
            return {
                ...prev,
                supplierCompany: +value,
                supplierUser: null,
                supplierAddress: null,
                availableUsers: null,
                availableAddresses: null,
            };
        }, this.loadData);
    }

    isDirtyField(field) {
        const oldValue = this.getVehicleProperty(field);
        return oldValue !== this.state[field];
    }

    isDirtyForm() {
        let isDirty = false;

        for (const field of this.fieldsList) {
            const isDirtyField = this.isDirtyField(field);
            isDirty = isDirtyField ? true : isDirty;
        }

        return isDirty;
    }

    toggleConfirmRemoveModal() {
        this.setState(prev => {
            let confirmRemove = !prev.confirmRemove;
            return {
                ...prev,
                confirmRemove,
            };
        });
    }

    toggleConfirmSaveModal() {
        this.setState(prev => {
            let confirmSave = !prev.confirmSave;
            return {
                ...prev,
                confirmSave,
            };
        });
    }

    reset() {
        const oldStateFields = this.fieldsList.reduce((state, field) => {
            state[field] = this.getVehicleProperty(field);
            return state;
        }, {});

        this.setState(prev => {
            return {
                ...prev,
                ...oldStateFields,
            };
        });
    }

    save() {
        const data = {
            supplierType: this.supplierType,
            supplierCompany: this.supplierCompany,
            supplierUser: this.supplierUser,
            supplierAddress: this.supplierAddress,
        }

        this.props.save(this.vehicle.id, data);
    }

    remove() {
        this.props.remove(this.vehicle.id);
    }

    render() {

        return (
            <Container>
                <Content>
                    <VBox>
                        <p>{this.lbl('vehicle.details.supplier.type.inputLbl')}</p>
                        <FormField>
                            <Dropdown options={this.supplierTypeOptions}
                                      onChange={this.onSupplierTypeChange.bind(this)}
                                      value={this.supplierType}/>
                        </FormField>
                    </VBox>
                    {this.renderExtraFields()}
                </Content>
                <HBox>
                    <Button primary disabled={this.resetDisabled} onClick={this.reset.bind(this)}>
                        {this.lbl('reset')}
                    </Button>
                    <Button primary disabled={this.removeDisabled} onClick={this.toggleConfirmRemoveModal.bind(this)}>
                        {this.lbl('remove')}
                    </Button>
                    <Button primary disabled={this.saveDisabled} onClick={this.toggleConfirmSaveModal.bind(this)}>
                        {this.lbl('save')}
                    </Button>
                </HBox>
                {this.renderDeleteConfirm()}
                {this.renderSaveConfirm()}
            </Container>
        );
    }

    renderExtraFields() {
        switch (this.supplierType) {
            case SUPPLIER_TYPES.INDIVIDUAL:
                return this.renderIndividualSupplierFields();
            case SUPPLIER_TYPES.COMPANY:
                return this.renderCompanySupplierFields();
            default:
                return null;
        }
    }

    renderIndividualSupplierFields() {
        return (
            <Container>
                <VBox>
                    <p>{this.lbl('vehicle.details.supplier.user.inputLbl')}</p>
                    <FormField>
                        {this.renderUsersDropdown()}
                    </FormField>
                </VBox>
                {this.renderAddressField()}
            </Container>
        );
    }

    renderCompanySupplierFields() {
        return (
            <Container>
                <VBox>
                    <p>{this.lbl('vehicle.details.supplier.company.inputLbl')}</p>
                    <FormField>
                        {this.renderCompaniesDropdown()}
                    </FormField>
                </VBox>
                <VBox>
                    <p>{this.lbl('vehicle.details.supplier.companyUser.inputLbl')}</p>
                    <FormField>
                        {this.renderCompanyUsersDropdown()}
                    </FormField>
                </VBox>
                {this.renderAddressField()}
            </Container>
        );
    }

    renderAddressField() {
        return (
            <VBox>
                <p>{this.lbl('vehicle.details.supplier.address.inputLbl')}</p>
                <FormField>
                    {this.renderAddressesDropdown()}
                </FormField>
                {this.renderSelectedAddress()}
            </VBox>
        );
    }

    renderCompaniesDropdown() {
        if (this.props.companiesLoadWork) return <Loader small />;

        return (
            <Dropdown options={this.companyOptions}
                      onChange={this.onSupplierCompanyChange.bind(this)}
                      value={this.supplierCompany?.toString()}
            />
        )
    }

    renderCompanyUsersDropdown() {
        if (this.props.companyUsersLoadWork) return <Loader small />;

        return (
            <Dropdown options={this.usersOptions}
                      onChange={this.onSupplierUserChange.bind(this)}
                      value={this.supplierUser?.toString()}
            />
        )
    }

    renderUsersDropdown() {
        if (this.props.usersLoadWork) return <Loader small />;

        return (
            <Dropdown options={this.usersOptions}
                      onChange={this.onSupplierUserChange.bind(this)}
                      value={this.supplierUser?.toString()}
            />
        )
    }

    renderAddressesDropdown() {
        if (this.props.addressesLoadWork) return <Loader small />;

        return (
            <Dropdown options={this.addressesOptions}
                      onChange={this.onSupplierAddressChange.bind(this)}
                      value={this.supplierAddress?.toString()}
            />
        )
    }

    renderSelectedAddress() {
        return (
            this.address &&
            <Address address={this.address}/>
        );
    }

    renderDeleteConfirm() {
        return (
            this.state.confirmRemove &&
            <Modal key="remove" onClose={this.toggleConfirmRemoveModal.bind(this)} title={this.lbl('vehicle.details.option.modal.deleteConfirmationTitle')}>
                <div>
                    {this.lbl('vehicle.details.supplier.modal.deleteConfirmationTitle')}
                    <br/>
                    <Button onClick={this.remove.bind(this)}>{this.lbl('remove')}</Button>
                </div>
            </Modal>
        );
    }

    renderSaveConfirm() {
        return (
            this.state.confirmSave &&
            <Modal key="save" onClose={this.toggleConfirmSaveModal.bind(this)} title={this.lbl('vehicle.details.option.modal.deleteConfirmationTitle')}>
                <div>
                    {this.lbl('vehicle.details.supplier.modal.saveConfirmationTitle')}
                    <br/>
                    <Button onClick={this.save.bind(this)}>{this.lbl('save')}</Button>
                </div>
            </Modal>
        );
    }
}

DetailsSupplierComp.propTypes = {
    dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) =>
    createStructuredSelector({
        usersLoadWork: selectWorks([REQUEST_USERS]),
        companyUsersLoadWork: selectWorks([REQUEST_COMPANY]),
        companiesLoadWork: selectWorks([REQUEST_COMPANIES]),
        addressesLoadWork: selectWorks([REQUEST_USER]),
    });

function mapDispatchToProps(dispatch, ownProps) {
    return {
        dispatch,
        fetchCompanies: (offset, limit, callback) => {
            dispatch(fetchCompanies(offset, limit, callback));
        },
        fetchUsers: (offset, limit, callback) => {
            dispatch(fetchUsersList(offset, limit, null, null, callback));
        },
        fetchCompany: (companyId, callback) => {
            dispatch(fetchCompany(companyId, callback));
        },
        fetchUser: (userId, callback) => {
            dispatch(fetchUser(userId, callback));
        },
        save: (vehicleId, data) => {
            dispatch(addSupplier(vehicleId, data));
        },
        remove: (vehicleId, data) => {
            dispatch(removeSupplier(vehicleId, data));
        },
    };
}

const withConnect = connect(
    mapStateToProps,
    mapDispatchToProps,
);

export const DetailsSupplier = compose(
    withConnect,
    withRouter,
)(DetailsSupplierComp);
